/** Persists the most recent spot LTP per Kite symbol to localStorage so the
 * Market Status row can keep showing the spot + ATM even when the broker
 * session is down (overnight, weekends, after a logout).
 *
 * Cache is best-effort: localStorage might be disabled (private mode,
 * quota issues). Read/write are wrapped in try/catch and return null on
 * failure rather than throwing.
 */

const STORAGE_PREFIX = "nss.spot.";

export interface CachedSpot {
  value: number;
  /** Epoch millis when this value was written. */
  ts: number;
}

export function readCachedSpot(symbol: string): CachedSpot | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + symbol);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isCachedSpot(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedSpot(symbol: string, value: number): void {
  try {
    const payload: CachedSpot = { value, ts: Date.now() };
    window.localStorage.setItem(STORAGE_PREFIX + symbol, JSON.stringify(payload));
  } catch {
    // Quota or disabled — silently ignore; we'll just re-fetch next time.
  }
}

function isCachedSpot(v: unknown): v is CachedSpot {
  if (typeof v !== "object" || v === null) return false;
  const rec = v as Record<string, unknown>;
  return typeof rec.value === "number" && typeof rec.ts === "number";
}

export function formatStaleHint(ts: number, now: number): string {
  const mins = Math.floor((now - ts) / 60_000);
  if (mins < 1) return "Last seen just now";
  if (mins < 60) return `Last seen ${String(mins)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Last seen ${String(hours)}h ago`;
  const days = Math.floor(hours / 24);
  return `Last seen ${String(days)}d ago`;
}
